/**
 * Web Push subscription helper.
 *
 * Wires Firebase Cloud Messaging on the web for the broadcast system.
 * The VAPID public key is a public credential (Phase 0); the matching
 * private side lives only in the Firebase project (server fan-out uses
 * FIREBASE_SERVICE_ACCOUNT_JSON inside the edge function).
 *
 * Usage:
 *   const ok = await enablePushNotifications(userId)
 */
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { supabase } from "@/integrations/supabase/client";

// Public Firebase config — non-secret. messagingSenderId + appId are
// the only fields FCM actually requires.
const firebaseConfig = {
  apiKey: "AIzaSyA-public-placeholder",
  authDomain: "wavlynq.firebaseapp.com",
  projectId: "wavlynq",
  storageBucket: "wavlynq.appspot.com",
  messagingSenderId: "1086385777099",
  appId: "1:1086385777099:web:placeholder",
};

const VAPID_PUBLIC_KEY =
  "BF2xznhLBYiLGUmhCjvLBXRurC0UBFeabgKOJscrMf7Dnq9SBZf-DhhWcfZgcYIt5qg8ClxHckGyqF165xV4jrs";

const initFirebase = () => {
  if (getApps().length) return getApps()[0]!;
  return initializeApp(firebaseConfig);
};

export const isPushSupported = async () => {
  if (typeof window === "undefined") return false;
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
};

export const currentPushPermission = (): NotificationPermission | "unsupported" => {
  if (typeof window === "undefined" || !("Notification" in window))
    return "unsupported";
  return Notification.permission;
};

/**
 * Prompts the user, registers the service worker, fetches the FCM token
 * and stores it on `public.push_subscriptions`. Idempotent — the unique
 * (user_id, fcm_token) constraint dedupes across calls.
 */
export const enablePushNotifications = async (
  userId: string,
): Promise<{ ok: true; token: string } | { ok: false; reason: string }> => {
  if (!(await isPushSupported())) {
    return { ok: false, reason: "Push isn't supported in this browser." };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "Notification permission was denied." };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
    );
    await navigator.serviceWorker.ready;
  } catch (e) {
    return { ok: false, reason: `Service worker failed: ${String(e)}` };
  }

  initFirebase();
  let token: string | null = null;
  try {
    token = await getToken(getMessaging(), {
      vapidKey: VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (e) {
    return { ok: false, reason: `Token request failed: ${String(e)}` };
  }
  if (!token) {
    return { ok: false, reason: "FCM did not return a token." };
  }

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
  const { error } = await supabase
    .from("push_subscriptions")
    .upsert(
      {
        user_id: userId,
        fcm_token: token,
        platform: "web",
        user_agent: ua,
        active: true,
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: "user_id,fcm_token" },
    );
  if (error) return { ok: false, reason: `Could not save token: ${error.message}` };

  return { ok: true, token };
};

export const disablePushNotifications = async (userId: string) => {
  await supabase
    .from("push_subscriptions")
    .update({ active: false })
    .eq("user_id", userId);
};
